import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Rocket, Phone, Mail, MapPin, Facebook, Instagram, Youtube, Linkedin } from "lucide-react";
import { api } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { trackEvent } from "@/lib/ga";

const TikTok = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M16.5 3c.3 2.2 1.6 3.8 3.7 4v2.5c-1.3 0-2.6-.4-3.7-1.1v6.6c0 3.4-2.7 6.1-6.1 6.1S4.3 18.4 4.3 15s2.7-6.1 6.1-6.1c.3 0 .6 0 .9.1v2.7c-.3-.1-.6-.1-.9-.1a3.4 3.4 0 1 0 3.4 3.4V3h2.7z" />
  </svg>
);

export default function Footer() {
  const { company } = useCompany();
  const { data: services = [] } = useQuery({ queryKey: ["footer-services"], queryFn: async () => (await api.get("/services")).data });
  const { data: locations = [] } = useQuery({ queryKey: ["footer-locations"], queryFn: async () => (await api.get("/locations")).data });
  const social = company.social || {};

  return (
    <footer className="relative bg-[#070a0f] text-slate-300" data-testid="site-footer">
      <div className="absolute inset-0 dot-grid opacity-40" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          <div className="col-span-2 md:col-span-1">
            <Link to="/" aria-label="Aquila Air & Heating home">
              <img src="/logo-full.webp" alt="Aquila Air & Heating — Comfort Ready for Liftoff" className="h-24 w-auto" width="1206" height="498" loading="lazy" decoding="async" />
            </Link>
            <p className="mt-4 text-sm leading-relaxed text-slate-400">Comfort Ready For Liftoff. Your local HVAC experts serving Cocoa and the Space Coast.</p>
            <div className="mt-4 flex gap-3">
              {social.facebook && <a href={social.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="footer-social-facebook" onClick={() => trackEvent("social_click", { network: "facebook" })} className="text-slate-400 hover:text-launch transition-colors"><Facebook className="h-5 w-5" /></a>}
              {social.tiktok && <a href={social.tiktok} target="_blank" rel="noopener noreferrer" aria-label="TikTok" data-testid="footer-social-tiktok" onClick={() => trackEvent("social_click", { network: "tiktok" })} className="text-slate-400 hover:text-launch transition-colors"><TikTok className="h-5 w-5" /></a>}
              {social.instagram && <a href={social.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="footer-social-instagram" onClick={() => trackEvent("social_click", { network: "instagram" })} className="text-slate-400 hover:text-launch transition-colors"><Instagram className="h-5 w-5" /></a>}
              {social.youtube && <a href={social.youtube} target="_blank" rel="noopener noreferrer" aria-label="YouTube" data-testid="footer-social-youtube" className="text-slate-400 hover:text-launch transition-colors"><Youtube className="h-5 w-5" /></a>}
              {social.linkedin && <a href={social.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" data-testid="footer-social-linkedin" className="text-slate-400 hover:text-launch transition-colors"><Linkedin className="h-5 w-5" /></a>}
            </div>
            <img
              src="/locally-owned-badge.png"
              alt="Locally Owned & Operated — Certified by American Operator"
              className="mt-6 h-32 w-auto drop-shadow-lg"
              width="848"
              height="1264"
              loading="lazy"
              decoding="async"
              data-testid="footer-locally-owned-badge"
            />
          </div>

          <div>
            <h3 className="font-heading font-semibold text-white mb-3">Services</h3>
            <ul className="space-y-2 text-sm">
              {services.slice(0, 8).map((s) => (
                <li key={s.slug}><Link to={`/${s.slug}`} className="hover:text-launch transition-colors">{s.name}</Link></li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-heading font-semibold text-white mb-3">Service Areas</h3>
            <ul className="space-y-2 text-sm">
              {locations.slice(0, 8).map((l) => (
                <li key={l.slug}><Link to={`/service-areas/${l.slug}`} className="hover:text-launch transition-colors">{l.city}, FL</Link></li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-heading font-semibold text-white mb-3">Contact</h3>
            <ul className="space-y-3 text-sm">
              <li><a href={`tel:${company.phone_href || ""}`} onClick={() => trackEvent("phone_click", { location: "footer" })} className="flex items-center gap-2 hover:text-launch transition-colors"><Phone className="h-4 w-4 text-launch" /> {company.phone}</a></li>
              <li><a href={`mailto:${company.email || ""}`} onClick={() => trackEvent("email_click", { location: "footer" })} className="flex items-center gap-2 hover:text-launch transition-colors"><Mail className="h-4 w-4 text-launch" /> {company.email}</a></li>
              <li className="flex items-center gap-2"><MapPin className="h-4 w-4 text-launch" /> {company.address}</li>
              <li className="text-slate-400 pt-1">{company.hours}</li>
            </ul>
            <div className="mt-4 flex flex-wrap gap-3 text-xs">
              <Link to="/maintenance-plan" className="hover:text-launch">Maintenance</Link>
              <Link to="/lifetime-warranty" className="hover:text-launch">Lifetime Warranty</Link>
              <Link to="/guarantees" className="hover:text-launch">Guarantees</Link>
              <a href="https://toddkellerfl.com" target="_blank" rel="noopener noreferrer" className="hover:text-launch">Todd Keller</a>
              <a href="https://piperoutdoors.com" target="_blank" rel="noopener noreferrer" className="hover:text-launch">Piper Outdoors</a>
              <Link to="/careers" className="hover:text-launch">Careers</Link>
              <Link to="/contact-us" className="hover:text-launch">Contact</Link>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-white/10 flex flex-col sm:flex-row justify-between items-center gap-3 text-xs text-slate-400">
          <p>&copy; {new Date().getFullYear()} Aquila Air &amp; Heating. {company.license}. All rights reserved.</p>
          <p>Serving {company.primary_service_area}</p>
        </div>
      </div>
    </footer>
  );
}
